import React, { useState, useEffect, useCallback, useRef } from 'react';
import { spanish } from '../../api';
import { playSpanish } from '../../utils/spanishAudio';
import SpanishPractice from './SpanishPractice';
import SpanishExam from './SpanishExam';
import './Spanish.css';

const STATUS_LABEL = { new: 'New', learning: 'Learning', known: 'Learned', mastered: 'Mastered' };

export default function SpanishTab({ onOpenSettings }) {
  const [data, setData] = useState(null);
  const [today, setToday] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});
  const [practice, setPractice] = useState(null); // { unit?, itemIds? }
  const [bonusMsg, setBonusMsg] = useState('');
  const [examLevel, setExamLevel] = useState(null);
  const [examNote, setExamNote] = useState('');
  const [reveal, setReveal] = useState(null);   // level that was just opened by an exam - scrolled to and shown off
  const scrolledFor = useRef(null);
  // A level whose exam is passed can be folded away. Default: folded (the next level takes the stage); remembered per device.
  const [folds, setFolds] = useState(() => { try { return JSON.parse(localStorage.getItem('spanishLevelFolds') || '{}'); } catch (e) { return {}; } });
  const isFolded = (c) => !!c.passed && !c.locked && (folds[c.n] === undefined ? true : !!folds[c.n]);
  const toggleFold = (c) => setFolds(prev => {
    const next = { ...prev, [c.n]: !isFolded(c) };
    try { localStorage.setItem('spanishLevelFolds', JSON.stringify(next)); } catch (e) { /* not remembered */ }
    return next;
  });

  const load = useCallback(async () => {
    try {
      const [me, td] = await Promise.all([spanish.me(), spanish.today()]);
      setData(me.data);
      setToday(td.data.card);
      setError('');
    } catch (e) {
      setError('Could not load Spanish. Please try again.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // After a pass: once the new level is unlocked in the data, scroll to it (the CSS animates it in).
  // The scroll is only "done" once it really happened, so a data refresh in between just reschedules it.
  useEffect(() => {
    if (!reveal || !data || scrolledFor.current === reveal) return undefined;
    const course = (data.courses || []).find(c => c.n === reveal);
    if (!course || course.locked) return undefined;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const t = setTimeout(() => {
      const el = document.getElementById('sp-course-' + reveal);
      if (el) { scrolledFor.current = reveal; el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }); }
    }, 800);
    return () => clearTimeout(t);
  }, [reveal, data]);

  // the highlight / banner fade away after a few seconds
  useEffect(() => {
    if (!reveal) return undefined;
    const t = setTimeout(() => { setReveal(null); scrolledFor.current = null; }, 9000);
    return () => clearTimeout(t);
  }, [reveal]);

  const claim = async (card) => {
    try {
      const res = await spanish.claimToday(card.logId);
      if (res.data.xp) setBonusMsg(`🎁 Lesson bonus: +${res.data.xp} XP!`);
      load();
    } catch (e) {
      setBonusMsg('Practise every phrase first, then claim your bonus.');
    }
  };

  const closePractice = () => { setPractice(null); load(); };
  const closeExam = async (result) => {
    setExamLevel(null);
    if (result) setExamNote(result.openedLevel ? '' : result.finishedAllLevels ? '🌟 You have completed every level!' : '');
    await load();                                   // the new level is open in the data before we scroll to it
    if (result && result.openedLevel) setReveal(result.openedLevel);
  };

  if (error) return <div className="sp-wrap"><div className="sp-error">{error}</div><button className="sp-btn" onClick={load}>Retry</button></div>;
  if (!data) return <div className="sp-wrap"><div className="sp-empty">Loading…</div></div>;

  if (!data.enabled) {
    return (
      <div className="sp-wrap">
        <div className="sp-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 44 }}>🇪🇸</div>
          <h3>Padel Español is off</h3>
          <p className="sp-sub">Turn it on in Settings to learn Spanish through padel.</p>
          {onOpenSettings && <button className="sp-btn" onClick={onOpenSettings}>Open Settings</button>}
        </div>
      </div>
    );
  }

  const { units, summary } = data;
  const courses = data.courses && data.courses.length ? data.courses : [{ n: 1, title: 'Spanish', blurb: '', locked: false }];
  // the level the student is working on: the first unlocked level whose exam is not passed yet
  const heroCourse = courses.find(c => !c.locked && !c.passed) || courses[courses.length - 1];

  const examCard = (c) => (
    <div className={`sp-card sp-exam-card ${c.passed ? 'sp-exam-card--passed' : ''}`}>
      <div className="sp-exam-card-head">
        <h3>📝 Level {c.n} exam</h3>
        {c.passed && <span className="sp-chip sp-chip--known">Passed · best {c.bestPercent}%</span>}
        {!c.passed && c.lastExam && <span className="sp-chip sp-chip--learning">Last {c.lastExam.percent}%</span>}
      </div>
      {c.examReady ? (
        <>
          <p className="sp-sub">
            {c.examSize} questions from the Level {c.n} words you have studied. Pass with {c.passPercent}%{c.n < courses.length ? ` to open Level ${c.n + 1}` : ''}.
          </p>
          <button className="sp-btn" onClick={() => setExamLevel(c.n)}>{c.passed ? 'Take it again' : c.attempts ? 'Try again' : 'Start exam'}</button>
        </>
      ) : (
        <>
          <p className="sp-sub">Study {c.minStudied} Level {c.n} words to open the exam. You have studied {c.studied}.</p>
          <div className="sp-bar"><i style={{ width: `${Math.min(100, Math.round((c.studied / Math.max(1, c.minStudied)) * 100))}%` }} /></div>
        </>
      )}
    </div>
  );

  const renderUnit = (u, idx = 0) => (
    <div className="sp-card" key={u.key} style={{ '--i': idx }}>
      <button className="sp-unit-head" onClick={() => setOpen(o => ({ ...o, [u.key]: !o[u.key] }))} aria-expanded={!!open[u.key]}>
        <span className="sp-unit-icon">{u.icon}</span>
        <span style={{ flex: 1 }}>
          <div className="sp-unit-title">{u.title}</div>
          <div className="sp-unit-meta">{u.known}/{u.total} learned · {u.blurb}</div>
          <div className="sp-bar"><i style={{ width: `${u.percent}%` }} /></div>
        </span>
        <span>{open[u.key] ? '▲' : '▼'}</span>
      </button>
      {open[u.key] && (
        <div className="sp-unit-body">
          <button className="sp-btn sp-btn--ghost sp-btn--sm" onClick={() => setPractice({ unit: u.key })}>Practise this unit</button>
          {u.items.map(it => (
            <div className="sp-row" key={it._id}>
              <div className="sp-phrase-main">
                <div className="sp-es" style={{ fontSize: 15 }}>{it.es}</div>
                <div className="sp-en">{it.en}</div>
              </div>
              {it.awaitingApproval && <span className="sp-hw" title="Your coach picked this word - they confirm when you've learned it">🎯 Session word</span>}
              <span className={`sp-chip sp-chip--${it.status}`}>{STATUS_LABEL[it.status]}</span>
              <button className="sp-play" onClick={() => playSpanish(it)} aria-label={`Listen to ${it.es}`}>🔊</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="sp-wrap">
      <div className="sp-hero">
        <h2>🇪🇸 Padel Español</h2>
        <p>Learn Spanish with the words you hear on court.</p>
        <div className="sp-hero-stats">
          <div className="sp-stat"><b>{summary.currentLevel}</b><span>Level</span></div>
          <div className="sp-stat"><b>{summary.known}/{summary.total}</b><span>Words learned</span></div>
          <div className="sp-stat"><b>{summary.streak}🔥</b><span>Day streak</span></div>
          <div className="sp-stat"><b>{summary.xp}</b><span>XP</span></div>
        </div>
        {heroCourse && (
          <>
            <div className="sp-level-bar"><i style={{ width: `${heroCourse.passed ? 100 : Math.min(100, Math.round((heroCourse.studied / Math.max(1, heroCourse.minStudied)) * 100))}%` }} /></div>
            <div className="sp-level-label">
              {summary.allPassed
                ? '🌟 Every level completed'
                : heroCourse.passed
                  ? `Level ${heroCourse.n} exam passed · best ${heroCourse.bestPercent}%`
                  : heroCourse.examReady
                    ? `Level ${heroCourse.n} exam is ready - take it below`
                    : `Study ${heroCourse.minStudied - heroCourse.studied} more Level ${heroCourse.n} words to open the exam`}
            </div>
          </>
        )}
      </div>

      {today && (
        <div className="sp-card sp-today">
          <h3>🎾 Phrase of the day</h3>
          <p className="sp-sub">Today you heard these in your lesson.</p>
          {today.items.map(it => (
            <div className="sp-phrase" key={it._id}>
              <div className="sp-phrase-main">
                <div className="sp-es">{it.es} {it.practiced && <span className="sp-check">✓</span>}</div>
                <div className="sp-en">{it.en}</div>
                {it.example && <div className="sp-ex">“{it.example}”</div>}
              </div>
              <button className="sp-play" onClick={() => playSpanish(it)} aria-label={`Listen to ${it.es}`}>🔊</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {today.claimed ? (
              <span className="sp-check">🎁 Bonus claimed</span>
            ) : today.allPracticed ? (
              <button className="sp-btn sp-btn--ok" onClick={() => claim(today)}>🎁 Claim +{today.bonusXp} XP bonus</button>
            ) : (
              <button className="sp-btn" onClick={() => setPractice({ itemIds: today.items.map(i => i._id), unit: undefined })}>
                Practise them · +{today.bonusXp} XP bonus
              </button>
            )}
          </div>
          {bonusMsg && <div className="sp-feedback sp-feedback--ok">{bonusMsg}</div>}
        </div>
      )}

      <div className="sp-card" style={{ textAlign: 'center' }}>
        <button className="sp-btn" onClick={() => setPractice({})}>▶ Daily practice</button>
        <p className="sp-sub" style={{ margin: '8px 0 0' }}>
          {summary.practicedToday ? 'You practised today - keep it going!' : 'Three minutes a day keeps your streak alive.'}
        </p>
      </div>

      {courses.map(c => {
        const courseUnits = units.filter(u => (u.course || 1) === c.n);
        const canFold = !!c.passed && !c.locked;
        const folded = isFolded(c);
        return (
          <section key={c.n} id={`sp-course-${c.n}`} className={`sp-course ${reveal === c.n && !c.locked ? 'sp-course--reveal' : ''}`} aria-label={c.title}>
            {reveal === c.n && !c.locked && (
              <div className="sp-unlock-banner" role="status">
                <span className="sp-lock-swap" aria-hidden="true"><i>🔒</i><i>🔓</i></span>
                <span>Level {c.n} unlocked - {courseUnits.length} new units to explore</span>
              </div>
            )}
            {canFold ? (
              <button type="button" className="sp-course-toggle" onClick={() => toggleFold(c)} aria-expanded={!folded} aria-controls={`sp-course-body-${c.n}`}>
                <span className="sp-course-toggle-title">✅ {c.title}</span>
                <span className="sp-course-toggle-meta">
                  <span className="sp-chip sp-chip--known">Passed · best {c.bestPercent}%</span>
                  <span className="sp-course-count">{c.known}/{c.total}</span>
                  <span className="sp-chev" aria-hidden="true">▾</span>
                </span>
              </button>
            ) : (
              <div className="sp-course-head">
                <h3>{c.locked ? '🔒 ' : ''}{c.title}</h3>
                {c.total > 0 && <span className="sp-course-count">{c.known}/{c.total}</span>}
              </div>
            )}
            <div id={`sp-course-body-${c.n}`} className={`sp-collapse ${folded ? '' : 'is-open'}`}>
              <div className="sp-collapse-inner">
                {c.blurb && <p className="sp-sub">{c.blurb}</p>}
                {!c.locked && examCard(c)}
                {c.locked ? (
                  <div className="sp-card sp-locked">
                    <div className="sp-locked-title">Pass the Level {c.n - 1} exam to open Level {c.n}</div>
                    {(() => {
                      const prev = courses.find(x => x.n === c.n - 1);
                      if (!prev) return null;
                      return prev.examReady ? (
                        <>
                          <p className="sp-sub" style={{ margin: '4px 0 10px' }}>You've studied enough words - the Level {prev.n} exam is ready.</p>
                          <button className="sp-btn" onClick={() => setExamLevel(prev.n)}>Take the Level {prev.n} exam</button>
                        </>
                      ) : (
                        <>
                          <p className="sp-sub" style={{ margin: '4px 0 10px' }}>Study {prev.minStudied} Level {prev.n} words to open the exam ({prev.studied}/{prev.minStudied} so far).</p>
                          <div className="sp-bar"><i style={{ width: `${Math.min(100, Math.round((prev.studied / Math.max(1, prev.minStudied)) * 100))}%` }} /></div>
                        </>
                      );
                    })()}
                    <div className="sp-locked-units">
                      {courseUnits.map(u => <span key={u.key} className="sp-locked-chip">{u.icon} {u.title}</span>)}
                    </div>
                  </div>
                ) : (
                  <div className="sp-units">{courseUnits.map(renderUnit)}</div>
                )}
              </div>
            </div>
          </section>
        );
      })}

      {examNote && <div className="sp-unlocked" role="status">{examNote}</div>}

      {practice && <SpanishPractice unit={practice.unit} itemIds={practice.itemIds} onClose={closePractice} />}
      {examLevel && <SpanishExam level={examLevel} onClose={closeExam} />}
    </div>
  );
}
